import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { Box, Skeleton } from "@mui/material"
import type { Theme } from "@mui/material/styles"
import { makeStyles } from "tss-react/mui"

import { ViewType } from "./resourceBookingCalendars/BookingViewMenu"

/** Matches `resourceAreaWidth: 300` in `fullCalendarConfig`. */
const RESOURCE_LABEL_COLUMN_WIDTH = "18.75rem"

const HEADER_ROW_TIMELINE = "4.5rem"
const HEADER_ROW_WEEKLY = "3.75rem"
const GROUP_ROW_HEIGHT = "2.5rem"
const RESOURCE_ROW_HEIGHT = "3rem"

/** Weighted average row height (pattern group + 3× resource per cycle). */
const ROW_HEIGHT_AVG_REM = 11.5 / 4

const MIN_SKELETON_DATA_ROWS = 6
const MAX_SKELETON_DATA_ROWS = 48

const WEEKLY_DAY_COUNT = 7

type RowKind = "group" | "resource"

/** Repeating band: one group header + three resource rows (like real calendar). */
const ROW_PATTERN: readonly RowKind[] = ["group", "resource", "resource", "resource"]

function buildRowLayout(rowCount: number): RowKind[] {
    const n = Math.min(MAX_SKELETON_DATA_ROWS, Math.max(MIN_SKELETON_DATA_ROWS, Math.floor(rowCount)))
    return Array.from({ length: n }, (_, i) => ROW_PATTERN[i % ROW_PATTERN.length]!)
}

function buildDataRowsTemplate(layout: readonly RowKind[]) {
    return layout.map((row) => (row === "group" ? GROUP_ROW_HEIGHT : RESOURCE_ROW_HEIGHT)).join(" ")
}

function readRootFontSizePx(): number {
    if (typeof document === "undefined") return 16
    const px = parseFloat(getComputedStyle(document.documentElement).fontSize)
    return Number.isFinite(px) && px > 0 ? px : 16
}

function computeDataRowCountForHeight(mainHeightPx: number, viewType: ViewType, rootFontSizePx: number): number {
    if (mainHeightPx <= 0) return MIN_SKELETON_DATA_ROWS

    const headerRem = viewType === ViewType.TIMELINE ? 4.5 : 3.75
    const headerPx = headerRem * rootFontSizePx
    const availablePx = Math.max(0, mainHeightPx - headerPx)
    const avgRowPx = ROW_HEIGHT_AVG_REM * rootFontSizePx
    const raw = Math.floor(availablePx / avgRowPx)
    return Math.min(MAX_SKELETON_DATA_ROWS, Math.max(MIN_SKELETON_DATA_ROWS, raw))
}

function useSkeletonDataRowLayout(viewType: ViewType) {
    const mainRef = useRef<HTMLDivElement>(null)
    const [dataRowCount, setDataRowCount] = useState(MIN_SKELETON_DATA_ROWS)

    useLayoutEffect(() => {
        const el = mainRef.current
        if (!el) return

        const update = () => {
            const fs = readRootFontSizePx()
            setDataRowCount(computeDataRowCountForHeight(el.clientHeight, viewType, fs))
        }

        update()
        const ro = new ResizeObserver(() => {
            update()
        })
        ro.observe(el)
        return () => {
            ro.disconnect()
        }
    }, [viewType])

    const rowLayout = useMemo(() => buildRowLayout(dataRowCount), [dataRowCount])

    return { mainRef, rowLayout }
}

type GridPlacement = Readonly<{
    gridColumn: number
    gridRow: number | string
}>

const useGridPlacementStyles = makeStyles<GridPlacement>()((_, { gridColumn, gridRow }) => ({
    root: {
        gridColumn,
        gridRow,
        boxSizing: "border-box",
        minWidth: 0,
        minHeight: 0,
    },
}))

type RowTemplateProps = Readonly<{
    dataRowsTemplate: string
}>

const useRootStyles = makeStyles()(() => ({
    root: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
        overflow: "hidden",
    },
}))

function borderSlate200(theme: Theme) {
    const b = `1px solid ${theme.palette.tailwind.slate[200]}`
    return { borderRight: b, borderBottom: b }
}

const useStyles = makeStyles<RowTemplateProps>()((theme, { dataRowsTemplate }) => {
    const gridCommon = {
        display: "grid",
        flexGrow: 1,
        minHeight: 0,
        alignContent: "start",
    }

    const skeletonPill = {
        transform: "none",
        backgroundColor: theme.palette.tailwind.slate[200],
    }

    return {
        toolbar: {
            flexShrink: 0,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: ".25rem",
            padding: ".75rem 1rem",
            borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        },
        toolbarGroup: {
            display: "flex",
            alignItems: "center",
            gap: ".25rem",
        },
        toolbarButton: {
            borderRadius: ".625rem",
            transform: "none",
        },
        toolbarButtonSm: {
            width: "2.25rem",
            height: "2.25rem",
        },
        toolbarButtonMd: {
            width: "5.5rem",
            height: "2.25rem",
        },
        toolbarButtonLg: {
            width: "7.5rem",
            height: "2.25rem",
        },
        toolbarSpacer: {
            width: ".75rem",
            flexShrink: 0,
        },
        main: {
            flexGrow: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
        },
        timelineGrid: {
            ...gridCommon,
            gridTemplateColumns: `${RESOURCE_LABEL_COLUMN_WIDTH} minmax(0, 1fr)`,
            gridTemplateRows: `${HEADER_ROW_TIMELINE} ${dataRowsTemplate}`,
        },
        weeklyGrid: {
            ...gridCommon,
            gridTemplateColumns: `${RESOURCE_LABEL_COLUMN_WIDTH} repeat(${WEEKLY_DAY_COUNT}, minmax(0, 1fr))`,
            gridTemplateRows: `${HEADER_ROW_WEEKLY} ${dataRowsTemplate}`,
        },
        cornerCell: {
            gridColumn: 1,
            gridRow: 1,
            ...borderSlate200(theme),
            backgroundColor: theme.palette.tailwind.slate[50],
            boxSizing: "border-box",
            minWidth: 0,
            minHeight: 0,
        },
        timelineHeaderCell: {
            gridColumn: 2,
            gridRow: 1,
            display: "flex",
            flexDirection: "column",
            borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
            backgroundColor: theme.palette.common.white,
            boxSizing: "border-box",
            minWidth: 0,
            minHeight: 0,
        },
        timelineHeaderTitleRow: {
            height: "2.25rem",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 .5rem",
        },
        timelineHeaderTitle: {
            width: "40%",
            maxWidth: "12rem",
            height: "1rem",
            borderRadius: ".25rem",
            ...skeletonPill,
        },
        timelineHeaderHoursRow: {
            height: "2.25rem",
            boxSizing: "border-box",
            display: "grid",
            gridTemplateColumns: "repeat(9, minmax(0, 1fr))",
            alignItems: "center",
            justifyItems: "center",
            padding: "0 .75rem",
            columnGap: ".25rem",
            borderTop: `1px solid ${theme.palette.tailwind.slate[100]}`,
        },
        hourTick: {
            width: "80%",
            maxWidth: "1.75rem",
            height: ".875rem",
            borderRadius: ".125rem",
            ...skeletonPill,
        },
        groupRowLabel: {
            display: "flex",
            alignItems: "center",
            paddingLeft: ".5rem",
            paddingRight: ".5rem",
            gap: ".5rem",
            height: GROUP_ROW_HEIGHT,
            boxSizing: "border-box",
            backgroundColor: theme.palette.tailwind.slate[100],
            ...borderSlate200(theme),
        },
        groupExpander: {
            width: "1rem",
            height: "1rem",
            flexShrink: 0,
            borderRadius: ".125rem",
            ...skeletonPill,
        },
        groupTitle: {
            flexGrow: 1,
            height: "1rem",
            maxWidth: "60%",
            borderRadius: ".25rem",
            ...skeletonPill,
        },
        resourceRowLabel: {
            display: "flex",
            alignItems: "center",
            gap: ".5rem",
            padding: "0 .75rem 0 .25rem",
            height: RESOURCE_ROW_HEIGHT,
            boxSizing: "border-box",
            ...borderSlate200(theme),
            backgroundColor: theme.palette.common.white,
        },
        checkbox: {
            width: "1rem",
            height: "1rem",
            flexShrink: 0,
            borderRadius: ".125rem",
            ...skeletonPill,
        },
        resourceTitle: {
            flexGrow: 1,
            height: ".875rem",
            maxWidth: "75%",
            borderRadius: ".25rem",
            ...skeletonPill,
        },
        infoIcon: {
            width: "1rem",
            height: "1rem",
            flexShrink: 0,
            borderRadius: "50%",
            ...skeletonPill,
        },
        groupLane: {
            height: GROUP_ROW_HEIGHT,
            boxSizing: "border-box",
            ...borderSlate200(theme),
            backgroundColor: theme.palette.tailwind.slate[100],
        },
        timelineGroupLane: {
            opacity: 0.65,
        },
        timelineResourceLane: {
            height: RESOURCE_ROW_HEIGHT,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            padding: "0 .5rem",
            overflow: "hidden",
            backgroundColor: theme.palette.common.white,
            backgroundImage: `linear-gradient(
                90deg,
                ${theme.palette.tailwind.slate[200]} 0,
                ${theme.palette.tailwind.slate[200]} 1px,
                transparent 1px,
                transparent 100%
            )`,
            backgroundSize: "calc(100% / 9) 100%",
            borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        },
        eventBar: {
            width: "100%",
            height: "1.125rem",
            borderRadius: ".25rem",
            ...skeletonPill,
        },
        weeklyHeaderCell: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: ".5rem .25rem",
            ...borderSlate200(theme),
            backgroundColor: theme.palette.tailwind.slate[50],
            boxSizing: "border-box",
            minWidth: 0,
            minHeight: 0,
        },
        weeklyCellLast: {
            borderRight: "none",
        },
        weeklyDaySkeleton: {
            width: "70%",
            height: ".875rem",
            borderRadius: ".25rem",
            ...skeletonPill,
        },
        weeklyCellBody: {
            height: RESOURCE_ROW_HEIGHT,
            boxSizing: "border-box",
            ...borderSlate200(theme),
            padding: ".25rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: theme.palette.common.white,
            minWidth: 0,
        },
        weeklyCellBar: {
            width: "88%",
            height: ".875rem",
            borderRadius: ".25rem",
            ...skeletonPill,
        },
    }
})

type ResourceBookingCalendarSkeletonProps = Readonly<{
    viewType: ViewType
}>

function PlacedCell({
    gridColumn,
    gridRow,
    className,
    children,
}: Readonly<{
    gridColumn: number
    gridRow: number | string
    className: string
    children?: ReactNode
}>) {
    const { classes, cx } = useGridPlacementStyles({ gridColumn, gridRow })
    return <Box className={cx(classes.root, className)}>{children}</Box>
}

type SkeletonStyles = ReturnType<typeof useStyles>["classes"]

function SkeletonLabelRows({
    rowLayout,
    classes,
}: Readonly<{
    rowLayout: readonly RowKind[]
    classes: SkeletonStyles
}>) {
    return (
        <>
            {rowLayout.map((row, i) => {
                const r = 2 + i
                return row === "group" ? (
                    <PlacedCell key={`label-g-${i}`} gridColumn={1} gridRow={r} className={classes.groupRowLabel}>
                        <Skeleton variant="rounded" animation="wave" className={classes.groupExpander} />
                        <Skeleton variant="rounded" animation="wave" className={classes.groupTitle} />
                    </PlacedCell>
                ) : (
                    <PlacedCell key={`label-r-${i}`} gridColumn={1} gridRow={r} className={classes.resourceRowLabel}>
                        <Skeleton variant="rounded" animation="wave" className={classes.checkbox} />
                        <Skeleton variant="rounded" animation="wave" className={classes.resourceTitle} />
                        <Skeleton variant="circular" animation="wave" className={classes.infoIcon} />
                    </PlacedCell>
                )
            })}
        </>
    )
}

function TimelineViewSkeleton() {
    const { mainRef, rowLayout } = useSkeletonDataRowLayout(ViewType.TIMELINE)
    const dataRowsTemplate = useMemo(() => buildDataRowsTemplate(rowLayout), [rowLayout])
    const { classes, cx } = useStyles({ dataRowsTemplate })

    return (
        <>
            <Box className={classes.toolbar}>
                <Box className={classes.toolbarGroup}>
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        className={cx(classes.toolbarButton, classes.toolbarButtonSm)}
                    />
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        className={cx(classes.toolbarButton, classes.toolbarButtonMd)}
                    />
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        className={cx(classes.toolbarButton, classes.toolbarButtonSm)}
                    />
                </Box>
                <Box className={classes.toolbarSpacer} />
                <Skeleton
                    variant="rounded"
                    animation="wave"
                    className={cx(classes.toolbarButton, classes.toolbarButtonLg)}
                />
            </Box>
            <Box ref={mainRef} className={classes.main}>
                <Box className={classes.timelineGrid}>
                    <Box className={classes.cornerCell} />
                    <Box className={classes.timelineHeaderCell}>
                        <Box className={classes.timelineHeaderTitleRow}>
                            <Skeleton variant="rounded" animation="wave" className={classes.timelineHeaderTitle} />
                        </Box>
                        <Box className={classes.timelineHeaderHoursRow}>
                            {Array.from({ length: 9 }).map((_, h) => (
                                <Skeleton key={h} variant="rounded" animation="wave" className={classes.hourTick} />
                            ))}
                        </Box>
                    </Box>

                    <SkeletonLabelRows rowLayout={rowLayout} classes={classes} />

                    {rowLayout.map((row, i) => {
                        const r = 2 + i
                        if (row === "group") {
                            return (
                                <PlacedCell
                                    key={`lane-g-${i}`}
                                    gridColumn={2}
                                    gridRow={r}
                                    className={cx(classes.groupLane, classes.timelineGroupLane)}
                                />
                            )
                        }
                        return (
                            <PlacedCell
                                key={`lane-r-${i}`}
                                gridColumn={2}
                                gridRow={r}
                                className={classes.timelineResourceLane}
                            >
                                <Skeleton variant="rounded" animation="wave" className={classes.eventBar} />
                            </PlacedCell>
                        )
                    })}
                </Box>
            </Box>
        </>
    )
}

function WeeklyViewSkeleton() {
    const { mainRef, rowLayout } = useSkeletonDataRowLayout(ViewType.AVAILABLE_TIME)
    const dataRowsTemplate = useMemo(() => buildDataRowsTemplate(rowLayout), [rowLayout])
    const { classes, cx } = useStyles({ dataRowsTemplate })

    return (
        <>
            <Box className={classes.toolbar}>
                <Skeleton
                    variant="rounded"
                    animation="wave"
                    className={cx(classes.toolbarButton, classes.toolbarButtonSm)}
                />
                <Skeleton
                    variant="rounded"
                    animation="wave"
                    className={cx(classes.toolbarButton, classes.toolbarButtonLg)}
                />
                <Skeleton
                    variant="rounded"
                    animation="wave"
                    className={cx(classes.toolbarButton, classes.toolbarButtonSm)}
                />
            </Box>
            <Box ref={mainRef} className={classes.main}>
                <Box className={classes.weeklyGrid}>
                    <Box className={classes.cornerCell} />
                    {Array.from({ length: WEEKLY_DAY_COUNT }).map((_, d) => (
                        <PlacedCell
                            key={d}
                            gridColumn={2 + d}
                            gridRow={1}
                            className={cx(
                                classes.weeklyHeaderCell,
                                d === WEEKLY_DAY_COUNT - 1 && classes.weeklyCellLast,
                            )}
                        >
                            <Skeleton variant="rounded" animation="wave" className={classes.weeklyDaySkeleton} />
                        </PlacedCell>
                    ))}
                    <SkeletonLabelRows rowLayout={rowLayout} classes={classes} />
                    {rowLayout.map((row, i) => {
                        const r = 2 + i
                        if (row === "group") {
                            return Array.from({ length: WEEKLY_DAY_COUNT }).map((_, d) => (
                                <PlacedCell
                                    key={`${i}-${d}`}
                                    gridColumn={2 + d}
                                    gridRow={r}
                                    className={cx(
                                        classes.groupLane,
                                        d === WEEKLY_DAY_COUNT - 1 && classes.weeklyCellLast,
                                    )}
                                />
                            ))
                        }
                        return Array.from({ length: WEEKLY_DAY_COUNT }).map((_, d) => (
                            <PlacedCell
                                key={`${i}-${d}`}
                                gridColumn={2 + d}
                                gridRow={r}
                                className={cx(
                                    classes.weeklyCellBody,
                                    d === WEEKLY_DAY_COUNT - 1 && classes.weeklyCellLast,
                                )}
                            >
                                <Skeleton variant="rounded" animation="wave" className={classes.weeklyCellBar} />
                            </PlacedCell>
                        ))
                    })}
                </Box>
            </Box>
        </>
    )
}

export function ResourceBookingCalendarSkeleton({ viewType }: ResourceBookingCalendarSkeletonProps) {
    const { classes } = useRootStyles()

    return (
        <Box className={classes.root}>
            {viewType === ViewType.TIMELINE ? <TimelineViewSkeleton /> : <WeeklyViewSkeleton />}
        </Box>
    )
}
